import clsx from "clsx";
import type { ClientTimelineEntry } from "@/services/api/agentic-v2";
import { parseApiDate } from "@/components/missions/MissionRow";

/**
 * Vertical event-stream view for one client.
 *
 * Each row: date column, coloured dot, title + optional detail + chips.
 * Colour is derived from the entry kind / actor_type so the user can
 * eyeball categories without reading.
 */
export interface ClientTimelineProps {
  entries: ClientTimelineEntry[];
}

export function ClientTimeline({ entries }: ClientTimelineProps) {
  if (entries.length === 0) {
    return (
      <div className="text-xs text-slate-500 py-6 text-center">
        No activity yet.
      </div>
    );
  }
  return (
    <ol className="flex flex-col">
      {entries.map((entry) => (
        <TimelineRow key={`${entry.kind}-${entry.id}`} entry={entry} />
      ))}
    </ol>
  );
}

function TimelineRow({ entry }: { entry: ClientTimelineEntry }) {
  const date = parseApiDate(entry.at);
  const tone = toneFor(entry);

  return (
    <li className="flex items-start gap-3 py-2.5 first:pt-0">
      <span className="text-[10px] text-slate-500 tabular-nums w-12 pt-1 flex-shrink-0">
        {date ? formatShortDate(date) : ""}
      </span>
      <span
        className={clsx(
          "mt-1.5 w-2 h-2 rounded-full flex-shrink-0",
          TONE_DOT[tone],
        )}
        aria-hidden
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-900 dark:text-slate-100">{entry.title}</div>
        {entry.detail && (
          <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 line-clamp-2">
            {entry.detail}
          </div>
        )}
        {entry.mission_name && (
          <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 mt-1">
            {entry.mission_name}
          </span>
        )}
      </div>
    </li>
  );
}

type Tone = "slate" | "indigo" | "emerald" | "amber" | "violet";

const TONE_DOT: Record<Tone, string> = {
  slate: "bg-slate-400 dark:bg-slate-500",
  indigo: "bg-indigo-400",
  emerald: "bg-emerald-400",
  amber: "bg-amber-400",
  violet: "bg-violet-400",
};

function toneFor(entry: ClientTimelineEntry): Tone {
  if (entry.kind === "memory") return "violet";
  // event titles we surface
  const title = entry.title.toLowerCase();
  if (title.includes("complete") || title.includes("approved")) return "emerald";
  if (title.includes("review") || title.includes("blocked")) return "amber";
  if (title.includes("mission")) return "indigo";
  return "slate";
}

function formatShortDate(d: Date): string {
  // "Apr 24"
  return d.toLocaleString(undefined, { month: "short", day: "numeric" });
}
